import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  getScreening,
  regenerateScreeningReport,
  downloadScreeningReportPdf,
  apiErrorMessage,
  STATUS_LABELS,
  VERDICT_LABELS,
} from "../api/screeningApi";
import { formatDateTime } from "../utils/formatters";
import "../css/Screening.css";

const POLL_MS = 4000;
const WAITING_STATUSES = ["SENT", "IN_PROGRESS", "COMPLETED"];

function BulletList({ items, kind }) {
  if (!items?.length) return <p className="scr-hint" style={{ margin: "4px 0 0" }}>None noted.</p>;
  return (
    <ul className={kind === "pos" ? "scr-pos" : "scr-neg"}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function AnswersSoFar({ screening }) {
  const answers = screening.answers || [];
  return (
    <div className="scr-panel">
      <h3 style={{ marginBottom: "6px" }}>Answers so far</h3>
      <p className="scr-hint">
        {answers.length} of {screening.questions.length} answered. Each answer appears here as soon as the candidate submits it.
      </p>
      {answers.length === 0 && <p className="scr-hint">No answers yet.</p>}
      {answers.map((a) => (
        <div className="scr-qa" key={a.qid}>
          <strong>{a.question}</strong>
          <blockquote>{a.skipped ? <em>Skipped</em> : a.answer}</blockquote>
          {!a.skipped && (
            <span className="scr-item-meta">
              {a.durationSec}s · {a.words} words · {a.wpm} wpm · {a.fillerCount} filler words
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function FullReport({ report }) {
  const s = report.sectionScores;
  const c = report.communication;
  const m = c.deliveryMetrics;
  const show = (n) => (n == null ? "—" : n);

  return (
    <>
      <div className="scr-panel">
        <div className="scr-panel-header">
          <h3>Overall</h3>
          <span className={`scr-verdict ${report.recommendation}`}>{VERDICT_LABELS[report.recommendation]}</span>
        </div>
        <div className="scr-scores">
          <div className="scr-score"><span>Overall score</span><b>{report.overallScore}</b></div>
          <div className="scr-score"><span>Technical ({report.weights.technical * 100}%)</span><b>{show(s.technical)}</b></div>
          <div className="scr-score"><span>Behavioural ({report.weights.behavioural * 100}%)</span><b>{show(s.behavioural)}</b></div>
          <div className="scr-score"><span>Communication ({report.weights.communication * 100}%)</span><b>{show(s.communication)}</b></div>
        </div>
        <p style={{ fontSize: "14px", margin: "8px 0 0" }}>{report.summary}</p>
      </div>

      <div className="scr-panel">
        <h3 style={{ marginBottom: "6px" }}>Resume claims to practise</h3>
        {report.claimsToPractise?.length ? (
          <>
            <p className="scr-hint">
              Claims on the optimized resume the candidate couldn't explain convincingly. A client interviewer will ask
              about these, so train on them first.
            </p>
            {report.claimsToPractise.map((c, i) => (
              <div className="scr-qa" key={i}>
                <strong>{c.claim}</strong>
                {c.issue && <div style={{ color: "#b3261e", marginTop: "4px" }}>{c.issue}</div>}
                {c.practise && <div style={{ marginTop: "4px" }}>Practise: {c.practise}</div>}
              </div>
            ))}
          </>
        ) : (
          <p className="scr-hint" style={{ margin: 0 }}>The candidate explained every resume claim they were asked about convincingly.</p>
        )}
      </div>

      <div className="scr-panel">
        <h3 style={{ marginBottom: "6px" }}>Where to focus</h3>
        <ul style={{ fontSize: "13px", paddingLeft: "18px", margin: 0 }}>
          {report.focusAreas.map((f, i) => (
            <li key={i} style={{ marginBottom: "4px" }}>{f}</li>
          ))}
        </ul>
      </div>

      <div className="scr-two">
        <div className="scr-panel">
          <h3>Technical</h3>
          <h4 style={{ margin: "12px 0 0", fontSize: "13px" }}>Positives</h4>
          <BulletList items={report.technical.positives} kind="pos" />
          <h4 style={{ margin: "12px 0 0", fontSize: "13px" }}>Negatives</h4>
          <BulletList items={report.technical.negatives} kind="neg" />
        </div>
        <div className="scr-panel">
          <h3>Behavioural</h3>
          <h4 style={{ margin: "12px 0 0", fontSize: "13px" }}>Positives</h4>
          <BulletList items={report.behavioural.positives} kind="pos" />
          <h4 style={{ margin: "12px 0 0", fontSize: "13px" }}>Negatives</h4>
          <BulletList items={report.behavioural.negatives} kind="neg" />
        </div>
      </div>

      <div className="scr-panel">
        <h3 style={{ marginBottom: "8px" }}>Communication and delivery</h3>
        <p style={{ fontSize: "13px", margin: "0 0 6px" }}>
          Clarity <strong>{c.clarity}/5</strong> · Structure <strong>{c.structure}/5</strong> · Relevance{" "}
          <strong>{c.relevance}/5</strong> · Completeness <strong>{c.completeness}/5</strong>
        </p>
        <ul style={{ fontSize: "13px", paddingLeft: "18px", margin: "0 0 8px" }}>
          {c.observations.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
        <span className="scr-item-meta">
          Answered {m.questionsAnswered}, skipped {m.questionsSkipped} · average answer {m.avgAnswerSeconds}s ·{" "}
          {m.avgWordsPerMinute} words per minute · {m.totalFillerWords} filler words in total
        </span>
      </div>

      <div className="scr-panel">
        <h3>Questions and answers</h3>
        {report.perQuestion.map((q, i) => (
          <div className="scr-qa" key={q.qid}>
            <div className="scr-qa-head">
              <strong>{i + 1}. {q.question}</strong>
              <span style={{ whiteSpace: "nowrap" }}>
                <span className={`scr-type ${q.type}`}>{q.type.toLowerCase()}</span>
                <strong>{q.rating}/5</strong>
              </span>
            </div>
            <blockquote>{q.skipped ? <em>Skipped</em> : q.answer}</blockquote>
            <span className="scr-item-meta">{q.feedback}</span>
          </div>
        ))}
      </div>

      <p className="scr-item-meta">
        Generated {formatDateTime(report.generatedAt)} with {report.model}. The AI assessment supports, and doesn't replace,
        the recruiter's judgement — listen back or re-interview when a score looks off.
      </p>
    </>
  );
}

export default function ScreeningReport() {
  const { id } = useParams();
  const [screening, setScreening] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    clearTimeout(timerRef.current);
    try {
      const { data } = await getScreening(id);
      setScreening(data.screening);
      setError("");
      // Keep refreshing while the candidate is answering or the report is being written.
      if (WAITING_STATUSES.includes(data.screening.status) && !data.screening.isExpired) {
        timerRef.current = setTimeout(load, POLL_MS);
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't load the screening."));
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => clearTimeout(timerRef.current);
  }, [load]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await regenerateScreeningReport(id);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't start the report."));
    } finally {
      setRetrying(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const response = await downloadScreeningReportPdf(id);
      // Browsers hide the server's file-name header across domains, so build the
      // same name here: e.g. "Rahul_Sharma_Screening_Report.pdf".
      const safeName = String(screening?.candidateId?.name || "Candidate").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_|_$/g, "");
      const fileName = `${safeName || "Candidate"}_Screening_Report.pdf`;

      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // With responseType "blob", the error message arrives as a Blob too.
      let message = "Couldn't download the PDF.";
      try {
        const text = await err?.response?.data?.text?.();
        message = JSON.parse(text).message || message;
      } catch { /* keep the default message */ }
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  if (error && !screening) {
    return (
      <div className="page-container">
        <div className="auth-error">{error}</div>
      </div>
    );
  }
  if (!screening) return <div className="page-container">Loading…</div>;

  const optimizationId = screening.optimizationId?._id || screening.optimizationId;

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>{screening.candidateId?.name} — screening report</h2>
        <Link to={`/dashboard/optimizations/${optimizationId}`}>Back to optimization</Link>
      </div>
      <p className="scr-hint" style={{ marginTop: "6px" }}>
        {screening.jobTitle}
        {screening.resumeVersionNumber != null && ` · based on final resume v${screening.resumeVersionNumber}`} ·{" "}
        <span className={`scr-status ${screening.isExpired ? "CANCELLED" : screening.status}`}>
          {screening.isExpired ? "Link expired" : STATUS_LABELS[screening.status]}
        </span>
        {screening.completedAt && ` · finished ${formatDateTime(screening.completedAt)}`}
      </p>

      {error && <div className="auth-error">{error}</div>}

      {screening.status === "COMPLETED" && (
        <p className="scr-hint">The interview is finished. The AI is writing the report — this page updates on its own.</p>
      )}

      {screening.status === "REPORT_FAILED" && (
        <div className="scr-error">
          The report couldn't be generated: {screening.reportError}
          <div className="scr-actions">
            <button type="button" onClick={handleRetry} disabled={retrying}>
              {retrying ? "Starting…" : "Generate report again"}
            </button>
          </div>
        </div>
      )}

      {screening.status === "REPORT_READY" && screening.report ? (
        <>
          <FullReport report={screening.report} />
          <div className="scr-actions" style={{ marginTop: 0 }}>
            <button type="button" onClick={handleDownload} disabled={downloading}>
              {downloading ? "Preparing PDF…" : "Download PDF"}
            </button>
            <button type="button" className="btn-secondary" onClick={handleRetry} disabled={retrying}>
              {retrying ? "Starting…" : "Regenerate report"}
            </button>
          </div>
        </>
      ) : (
        <AnswersSoFar screening={screening} />
      )}
    </div>
  );
}
